import { zodResolver } from "@hookform/resolvers/zod";
import merge from "lodash.merge";
import { useForm } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useParams } from "react-router-dom";
import { ProposedProjectController } from "services/proposedProject/controller";
import { FormRenderer, useAccordionWizard } from "stories";
import { AccordionForm } from "stories/components/accordionForm";
import { useFileUpload } from "utils/customHooks/useFileUpload";
import { useToast } from "utils/customHooks/useToast";
import { z } from "zod";
import { removeEmptyValues } from "../../../loanDetails/elements/borrower/LoanDetailsBorrower";
import { getEventItemModel } from "../eventChart/EventItem";
import { marketStrategyFormFields } from "./MarketStrategyFormFields";

const proposedProjectController = ProposedProjectController();

const marketingSchema = z
  .object({
    events: z.array(
      z
        .object({
          id: z.any().nullable(),
          name: z.string().nullable(),
          status: z.string().nullable(),
          start_date: z.string().nullable(),
          end_date: z.string().nullable(),
        })
        .partial()
        .passthrough()
        .refine(
          (data) => {
            return (
              !data.start_date ||
              !data.end_date ||
              new Date(data.start_date) <= new Date(data.end_date)
            );
          },
          {
            message: "The end date cannot be before the start date",
            path: ["end_date"],
          }
        )
    ),
  })
  .passthrough();

const getScheduleModel = () => ({
  events: [getEventItemModel()],
  reports: [],
  filename: null,
  gantt_chart: true,
  presale_status_report: null,
  presale_status_report_date: null,
  purchase_deposit_rate_amount: 0,
  purchase_deposit_rate_report: null,
  strategy_description: "",
  team: null,
});

export const MarketStrategyKey = "get-market-strategy";
const MarketStrategy = () => {
  const form = useForm({
    defaultValues: getScheduleModel(),
    resolver: zodResolver(marketingSchema),
  });
  const params = useParams();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const { next } = useAccordionWizard();
  const { handleFileFields } = useFileUpload();
  const queryKey = [MarketStrategyKey, { id: params.id }];
  const { isLoading: fetchLoading } = useQuery({
    queryKey,
    queryFn: () => proposedProjectController.getMarketingStrategy(params.id),
    onSuccess: (data) => {
      form.reset(merge(getScheduleModel(), data));
    },
  });

  const handleScheduleSubmit = async (values) => {
    delete values.report_files;
    return handleFileFields(values).then((values) => {
      values = removeEmptyValues(values);
      return proposedProjectController.updateMarketingStrategy({
        ...values,
        opportunityId: params.id,
      });
    });
  };

  const { mutate, isLoading: postLoading } = useMutation(handleScheduleSubmit, {
    onSuccess: () => {
      queryClient.invalidateQueries(queryKey);
      showToast("Market Strategy updated!", "success");
      next();
    },
  });

  return (
    <AccordionForm
      accordionKey="market-strategy"
      accordionTitle="Market Strategy Overview"
      form={form}
      isPostLoading={postLoading}
      isPreLoading={fetchLoading}
      onSubmit={mutate}
    >
      <FormRenderer fields={marketStrategyFormFields} />
    </AccordionForm>
  );
};

export default MarketStrategy;
